import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useLocalStorage } from 'react-use';
import { SIDEBAR_AUTO_COLLAPSE_VALUE } from 'styles/constants';

interface SidebarContextType {
  isCollapsed: boolean;
  toggleCollapsed: () => void;
  setCollapsed: (collapsed: boolean) => void;
  userPreference: boolean;
  isTemporaryOverride: boolean;
}

const SidebarContext = createContext<SidebarContextType>({
  isCollapsed: false,
  toggleCollapsed: () => {},
  setCollapsed: () => {},
  userPreference: false,
  isTemporaryOverride: false,
});

interface SidebarProviderProps {
  children: ReactNode;
}

export function SidebarProvider({ children }: SidebarProviderProps) {
  // User preference - what the user manually set
  const [userPreference, setUserPreference] = useLocalStorage('global-sidebar-user-preference', false);
  const userPreferenceValue = userPreference ?? false;

  // Temporary override - system-controlled state (e.g., based on screen size)
  const [temporaryOverride, setTemporaryOverride] = useState<boolean | null>(null);

  // Current effective state - either temporary override or user preference
  const isCollapsed = temporaryOverride !== null ? temporaryOverride : userPreferenceValue;

  useEffect(() => {
    const handleResize = () => {
      const newWidth = window.innerWidth;

      // At 1024px and below, force collapse if user preference was expanded
      if (newWidth <= SIDEBAR_AUTO_COLLAPSE_VALUE) {
        if (!userPreferenceValue) {
          setTemporaryOverride(true);
        }
      } else {
        // Above 1024px, restore user preference
        setTemporaryOverride(null);
      }
    };

    // Set initial state based on current screen size
    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [userPreferenceValue]);

  const toggleCollapsed = () => {
    const newUserPreference = !isCollapsed;
    setUserPreference(newUserPreference);

    // If we're in a temporary override state, clear it to use the new user preference
    if (temporaryOverride !== null) {
      setTemporaryOverride(null);
    }
  };

  const setCollapsed = (collapsed: boolean) => {
    setUserPreference(collapsed);

    // If we're in a temporary override state, clear it to use the new user preference
    if (temporaryOverride !== null) {
      setTemporaryOverride(null);
    }
  };

  const value: SidebarContextType = {
    isCollapsed,
    toggleCollapsed,
    setCollapsed,
    userPreference: userPreferenceValue,
    isTemporaryOverride: temporaryOverride !== null,
  };

  return <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>;
}

export function useSidebarState() {
  return useContext(SidebarContext);
}
